/**
 * Лист «Как посчитано» (docs/design/spend-2026-10-15).
 *
 * Открывается с «Можно тратить» и с трёх плиток под ним. Строки называются
 * как плитки, и сумма той, с которой пришли, на полторы секунды подсвечена:
 * человек сразу видит своё число в расчёте. Состав строк свёрнут, стрелка
 * справа; у строки, с которой пришли, он раскрыт. Пояснения — в (i), а не
 * строками под названием.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ZERO, formatMoney, type AvailableResult, type Money } from '@skoka/core';
import { Icon } from './icon.js';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { plural } from './text.js';
import { shortDay, spendSteps, type SpendFocus, type SpendLine } from './spend-steps.js';
import type { AppState } from './store.js';

/** Сколько горит подсветка суммы, с которой пришли. */
const FLASH_MS = 1500;
const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const longDay = (d: string): string => `${Number(d.slice(8, 10))} ${MONTHS[Number(d.slice(5, 7)) - 1]}`;
/** Вычитаемое — со знаком «−»; ноль — без него: «− 0 ₽» читается как ошибка. */
const minus = (m: Money) => (m === ZERO ? formatMoney(m) : `−\u00a0${formatMoney(m)}`);

export function SpendSheet({ state, result, focus, onClose }: {
  state: AppState;
  result: AvailableResult;
  focus: SpendFocus;
  onClose: () => void;
}) {
  const s = useMemo(() => spendSteps(state, result), [state, result]);
  const [open, setOpen] = useState<ReadonlySet<string>>(
    () => new Set(focus === 'acc' || focus === 'res' ? [focus] : []));
  const [flash, setFlash] = useState<SpendFocus | null>(focus);
  useEffect(() => {
    const t = setTimeout(() => setFlash(null), FLASH_MS);
    return () => clearTimeout(t);
  }, []);
  // Итог внизу длинного листа: показать его, а не верх расчёта.
  const lit = useRef<HTMLSpanElement>(null);
  useEffect(() => { lit.current?.scrollIntoView({ block: 'nearest' }); }, []);
  const toggle = (k: string) => setOpen((o) => {
    const next = new Set(o);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const amount = (k: string, text: string) => (
    <span ref={flash === k ? lit : undefined} className={`calc-amt num${flash === k ? ' flash' : ''}`}>{text}</span>
  );
  const row = (k: string, label: string, text: string, opts: { tip?: ReactNode; lines?: SpendLine[]; warn?: string } = {}) => {
    const lines = opts.lines ?? [];
    const opened = lines.length > 0 && open.has(k);
    const head = (
      <>
        <span className="calc-lbl">{label}{opts.tip && <> <Tip>{opts.tip}</Tip></>}</span>
        {amount(k, text)}
        <span className="calc-chev">{lines.length > 0 && <Icon name={opened ? 'chevron-up' : 'chevron-down'} size={11} />}</span>
      </>
    );
    return (
      <div className="calc-row" key={k}>
        {lines.length > 0
          ? <button className="calc-line" onClick={() => toggle(k)} aria-expanded={opened}>{head}</button>
          : <div className="calc-line">{head}</div>}
        {!opened && opts.warn && <div className="calc-warn">{opts.warn}</div>}
        {opened && lines.map((l) => (
          <div className="calc-det" key={l.key}>
            <span className="calc-ico"><Icon name={l.icon} size={11} /></span>
            <span className="calc-txt">
              {l.date ? `${l.label} · ${shortDay(l.date)}` : l.label}
              {l.tip && <> <Tip size={11}>{l.tip}</Tip></>}
              {l.late && <em> · не отмечен</em>}
            </span>
            <span className="num">{formatMoney(l.amount)}</span>
            <span className="calc-chev" />
          </div>
        ))}
      </div>
    );
  };
  const sum = (k: string, label: string, text: string, opts: { tip?: ReactNode; sub?: string; bad?: boolean } = {}) => (
    <div className={`calc-sum${opts.bad ? ' bad' : ''}`}>
      <div className="calc-line">
        <span className="calc-lbl">{label}{opts.tip && <> <Tip>{opts.tip}</Tip></>}</span>
        {amount(k, text)}
        <span className="calc-chev" />
      </div>
      {opts.sub && <div className="calc-sub">{opts.sub}</div>}
    </div>
  );

  const short = s.available < ZERO;
  const late = s.groups.find((g) => g.key === 'pay')?.lines.filter((l) => l.late) ?? [];
  const lateWarn = late.length === 1
    ? `Платёж «${late[0]!.label}» не отмечен — может, уже оплачен?`
    : late.length > 1 ? `${plural(late.length, 'платёж', 'платежа', 'платежей')} не отмечены — может, уже оплачены?` : '';
  const excluded = s.excluded.length > 0 ? ` Не входят: ${s.excluded.join(', ')}.` : '';

  return (
    <SheetWrap title="Как посчитано" onClose={onClose} className="calcsheet">
      <section className="calc-step">
        <h3 className="calc-h"><span className="calc-n">1</span>Сколько не отложено</h3>
        {row('acc', 'На счетах', formatMoney(s.onAccounts), {
          tip: `Только счета «для трат».${excluded}`, lines: s.accounts,
        })}
        {row('res', 'Отложено', minus(s.reservedTotal), {
          tip: 'Лежат на счетах, но уже под платежи и цели — эти деньги не тратим.', lines: s.reserved,
        })}
        {sum('free', 'Не отложено', formatMoney(s.free), { tip: 'На счетах минус отложено.' })}
      </section>
      <section className="calc-step">
        <h3 className="calc-h"><span className="calc-n">2</span>{s.until ? `Сколько можно тратить до ${longDay(s.until)}` : 'Сколько можно тратить'}</h3>
        {row('carry', 'Не отложено', formatMoney(s.free), { tip: 'Итог шага 1.' })}
        {s.groups.map((g) => row(g.key, g.label, minus(g.amount), {
          lines: g.lines, ...(g.key === 'pay' && lateWarn ? { warn: lateWarn } : {}),
        }))}
        {short
          ? sum('total', 'Не хватает', formatMoney(-s.available as Money), {
            bad: true, tip: `До ${longDay(s.window)} денег меньше, чем нужно на платежи и взносы.`,
          })
          : sum('total', 'Можно тратить', formatMoney(s.available), {
            ...(s.until && s.available > ZERO
              ? { sub: `на ${plural(s.days, 'день', 'дня', 'дней')} — ≈ ${formatMoney(s.perDay)} в день` } : {}),
          })}
      </section>
    </SheetWrap>
  );
}
